import { useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { useToast } from '@/components/ui/Toast';
import { useAppContext, useAsyncData } from '@/hooks';
import { institutionFormSchemasApi } from '@/api/config-governance';
import { FormSchemaEditor, type FormSchemaEditorApi } from '@/components/formSchemas/FormSchemaEditor';
import type { ChangeEvent, FormSchema } from '@/types/config-governance';
import styles from '../platform/platform-pages.module.css';

/**
 * ONE form schema version in the institution console — a second thin shell
 * over the shared FormSchemaEditor.
 *
 * Where the platform shell hands the plain platform API, this one binds the
 * institution-scoped API (institutionFormSchemasApi) to the institution from
 * the active admin context, and the editor's navigation props point back at
 * this console's register. That is the entire difference; the editor renders
 * the same builder, preview and lifecycle for both audiences.
 *
 * BASE LINKS LEAVE THE CONSOLE. An institution version's base is a platform
 * template, which lives in the platform console, not here — so baseHref is
 * the platform detail route, and the editor renders it as a real link rather
 * than plain text.
 */

export function AdminFormSchemaDetailPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { schemaId = '' } = useParams();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const fetcher = useCallback(
    () => institutionFormSchemasApi.get(institutionId, schemaId),
    [institutionId, schemaId],
  );
  const { state, reload, replace } = useAsyncData<FormSchema>(
    `institution-form-schema:${institutionId}:${schemaId}`,
    fetcher,
    !!institutionId,
  );

  const historyFetcher = useCallback(
    () => institutionFormSchemasApi.history(institutionId, schemaId),
    [institutionId, schemaId],
  );
  const history = useAsyncData<ChangeEvent[]>(
    `institution-form-schema-history:${institutionId}:${schemaId}`,
    historyFetcher,
    !!institutionId,
  );

  // The institution API is bound per-shell; the editor's api interface takes
  // the schema id as the first argument and lets the shell own the rest.
  const api: FormSchemaEditorApi = useMemo(() => bindInstitutionApi(institutionId), [institutionId]);

  if (!institutionId) return null;

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('studio.breadcrumbLabel')}>
        <Link to="/admin/institution/form-schemas">{t('formSchemas.title')}</Link>
        <Icon name="chevronRight" size={14} aria-hidden="true" />
        <span>{state.status === 'ready' ? state.data.code : '…'}</span>
      </nav>

      <AsyncPanel state={state} onRetry={reload}>
        {(schema) => (
          <FormSchemaEditor
            key={schema.id}
            schema={schema}
            replace={(next) => {
              replace(next);
              history.reload();
            }}
            historyState={history}
            onDeleted={() => navigate('/admin/institution/form-schemas')}
            toast={toast}
            api={api}
            detailFor={(id) => `/admin/institution/form-schemas/${encodeURIComponent(id)}`}
            baseHref={(id) => `/admin/form-schemas/${encodeURIComponent(id)}`}
          />
        )}
      </AsyncPanel>
    </div>
  );
}

function bindInstitutionApi(institutionId: string): FormSchemaEditorApi {
  return {
    update: (id, body) => institutionFormSchemasApi.update(institutionId, id, body),
    remove: (id) => institutionFormSchemasApi.remove(institutionId, id),
    create: (body) => institutionFormSchemasApi.create(institutionId, body),
    validate: (id) => institutionFormSchemasApi.validate(institutionId, id),
    transition: (id, toStatus, reason) =>
      institutionFormSchemasApi.transition(institutionId, id, toStatus, reason),
  };
}